// -----------------------------------------------------------
// FloraIQ - Mock Data: Fluxo Operacional
// -----------------------------------------------------------

export interface OperationalKpi {
  acoesEmAndamento: number;
  etapasConcluidas: number;
  pendenciasOperacionais: number;
  arvoresCadastradasCampo: number;
  levantamentos3DProcessados: number;
  validacoesAprovadas: number;
  produtosGerados: number;
  bloqueiosCriticos: number;
}

export const operationalKpis: OperationalKpi = {
  acoesEmAndamento: 7,
  etapasConcluidas: 42,
  pendenciasOperacionais: 18,
  arvoresCadastradasCampo: 12486,
  levantamentos3DProcessados: 18,
  validacoesAprovadas: 86,
  produtosGerados: 34,
  bloqueiosCriticos: 4,
};

export type StageStatus = "nao_iniciada" | "planejada" | "em_andamento" | "em_revisao" | "concluida" | "bloqueada" | "reaberta";

export interface StageInfo {
  id: number;
  nome: string;
  status: StageStatus;
  progresso: number;
  responsavel: string;
  dataPrevista: string;
  dataConcluida: string | null;
  pendencias: number;
  documentosVinculados: number;
}

export interface ChecklistItem {
  id: string;
  descricao: string;
  concluido: boolean;
  responsavel: string;
  prazo: string;
  observacao: string;
}

export interface PendingIssue {
  id: string;
  item: string;
  responsavel: string;
  prazo: string;
  prioridade: "alta" | "media" | "baixa";
  status: "pendente" | "em_andamento" | "resolvida";
}

export interface EquipmentItem {
  nome: string;
  disponivel: boolean;
  quantidade: number;
  observacao: string;
}

export interface BaseSurvey {
  id: string;
  nome: string;
  tipo: "drone_rgb" | "drone_lidar" | "laser_scanner" | "ortofoto" | "nuvem_fotogrametrica" | "gnss" | "base_cartografica";
  data: string;
  equipamento: string;
  areaCoberta: string;
  status: string;
  qualidade: number;
  arvoresPreIdentificadas: number;
}

export interface FieldProduction {
  tecnico: string;
  equipe: string;
  bairro: string;
  arvoresCadastradas: number;
  vistorias: number;
  fotos: number;
  pendencias: number;
  ultimaSincronizacao: string;
  status: string;
}

export interface Processing3D {
  id: string;
  levantamento: string;
  status: string;
  pontosProcessados: number;
  arvoresVinculadas: number;
  alturasExtraidas: number;
  copasEstimadas: number;
  interferenciasDetectadas: number;
  qualidade: number;
}

export interface LadmCompleteness {
  arvoresComUnidadeEspacial: number;
  arvoresComFonte: number;
  arvoresComResponsavel: number;
  arvoresComRRR: number;
  arvoresComHistorico: number;
  arvoresComDocumentos: number;
  arvoresComNuvemPontos: number;
  totalArvores: number;
}

export interface ValidationItem {
  id: string;
  regra: string;
  totalAvaliado: number;
  conformes: number;
  inconformes: number;
  severidade: string;
  status: string;
  acaoRecomendada: string;
}

export interface PublicationProduct {
  id: string;
  produto: string;
  tipo: string;
  status: string;
  visibilidade: "publico" | "privado";
  ultimaGeracao: string;
  responsavel: string;
}

export interface Deliverable {
  id: string;
  titulo: string;
  tipo: string;
  descricao: string;
  status: string;
  formato: string;
  ultimaAtualizacao: string;
}

export type CadastralActionStatus = "planejada" | "em_andamento" | "em_validacao" | "concluida" | "bloqueada" | "suspensa";

export interface CadastralAction {
  id: string;
  nome: string;
  municipio: string;
  uf: string;
  projetoVinculado: string;
  cliente: string;
  areaEstudo: string;
  tipoAcao: string;
  etapaAtual: string;
  progresso: number;
  arvoresPrevistas: number;
  arvoresCadastradas: number;
  responsavel: string;
  equipe: string[];
  status: CadastralActionStatus;
  pendencias: number;
  dataInicio: string;
  dataPrevistaConclusao: string;
  equipamentos: EquipmentItem[];
  stages: StageInfo[];
}

export const equipmentDefaults: EquipmentItem[] = [
  { nome: "Drone DJI Phantom 4 RTK", disponivel: true, quantidade: 1, observacao: "Baterias extras disponíveis" },
  { nome: "GPS/GNSS Trimble R2", disponivel: true, quantidade: 2, observacao: "Precisão centimétrica" },
  { nome: "Tablet Samsung Galaxy Tab Active", disponivel: true, quantidade: 5, observacao: "Coletor FloraIQ instalado" },
  { nome: "Câmera Nikon D5600", disponivel: true, quantidade: 2, observacao: "Lente 18-55mm" },
  { nome: "Laser Scanner FARO Focus", disponivel: false, quantidade: 1, observacao: "Em manutenção - previsão 10/07" },
  { nome: "LiDAR DJI Zenmuse L1", disponivel: true, quantidade: 1, observacao: "Acoplado ao drone Matrice 300" },
  { nome: "Trena digital Bosch GLM 50C", disponivel: true, quantidade: 3, observacao: "Aferida em 05/2026" },
  { nome: "EPIs (colete, capacete, luva)", disponivel: true, quantidade: 6, observacao: "Kits completos" },
  { nome: "Veículo de campo", disponivel: true, quantidade: 2, observacao: "Toyota Hilux - reserva confirmada" },
];

export const planningChecklist: ChecklistItem[] = [
  { id: "pl-01", descricao: "Definir área de estudo", concluido: true, responsavel: "Coordenador Técnico", prazo: "D-30", observacao: "Shapefile delimitado" },
  { id: "pl-02", descricao: "Definir unidades espaciais", concluido: true, responsavel: "Analista GIS", prazo: "D-28", observacao: "Setores, bairros, logradouros" },
  { id: "pl-03", descricao: "Preparar base cartográfica", concluido: true, responsavel: "Analista GIS", prazo: "D-25", observacao: "Ortofoto + camadas municipais" },
  { id: "pl-04", descricao: "Definir formulário de cadastro", concluido: true, responsavel: "Coordenador Técnico", prazo: "D-20", observacao: "Conforme FloraIQ-LADM" },
  { id: "pl-05", descricao: "Definir critérios técnicos", concluido: true, responsavel: "Engenheiro Florestal", prazo: "D-18", observacao: "DAP mínimo 3cm, altura > 1,3m" },
  { id: "pl-06", descricao: "Organizar equipe", concluido: false, responsavel: "Gerente de Projeto", prazo: "D-15", observacao: "Aguardando confirmação do terceiro técnico" },
  { id: "pl-07", descricao: "Organizar equipamentos", concluido: true, responsavel: "Coordenador de Campo", prazo: "D-10", observacao: "Laser scanner em manutenção" },
  { id: "pl-08", descricao: "Definir padrão de codificação", concluido: true, responsavel: "Analista de Dados", prazo: "D-10", observacao: "CTA-YYYY-NNNNNN" },
  { id: "pl-09", descricao: "Definir cronograma", concluido: false, responsavel: "Gerente de Projeto", prazo: "D-7", observacao: "Ajustando datas com a prefeitura" },
  { id: "pl-10", descricao: "Definir responsáveis", concluido: true, responsavel: "Gerente de Projeto", prazo: "D-7", observacao: "Matriz RACI concluída" },
  { id: "pl-11", descricao: "Definir produtos esperados", concluido: true, responsavel: "Coordenador Técnico", prazo: "D-5", observacao: "Conforme contrato" },
];

export const planningPendencias: PendingIssue[] = [
  { id: "pe-01", item: "Confirmar terceiro técnico de campo", responsavel: "RH", prazo: "05/07", prioridade: "alta", status: "pendente" },
  { id: "pe-02", item: "Aprovar cronograma com prefeitura", responsavel: "Gerente", prazo: "03/07", prioridade: "alta", status: "em_andamento" },
  { id: "pe-03", item: "Manutenção do laser scanner", responsavel: "TI", prazo: "10/07", prioridade: "media", status: "pendente" },
  { id: "pe-04", item: "Aquisição de baterias extras para tablet", responsavel: "Compras", prazo: "08/07", prioridade: "baixa", status: "pendente" },
];

export const baseSurveyChecklist: ChecklistItem[] = [
  { id: "lb-01", descricao: "Levantamento com drone", concluido: true, responsavel: "Piloto de Drone", prazo: "D+3", observacao: "Altura 80m, sobreposição 80/70" },
  { id: "lb-02", descricao: "Geração de ortomosaico", concluido: true, responsavel: "Processamento", prazo: "D+5", observacao: "Resolução 3cm/pixel" },
  { id: "lb-03", descricao: "Levantamento com LiDAR", concluido: false, responsavel: "Piloto de Drone", prazo: "D+10", observacao: "Aguardando liberação do laser scanner" },
  { id: "lb-04", descricao: "Geração de nuvem de pontos", concluido: false, responsavel: "Processamento", prazo: "D+12", observacao: "Depende do LiDAR" },
  { id: "lb-05", descricao: "Organização das camadas geográficas", concluido: true, responsavel: "Analista GIS", prazo: "D+7", observacao: "SHP + GeoJSON organizados" },
  { id: "lb-06", descricao: "Pré-identificação de árvores", concluido: true, responsavel: "Analista GIS", prazo: "D+14", observacao: "8.942 copas detectadas" },
  { id: "lb-07", descricao: "Validação da área coberta", concluido: true, responsavel: "Coordenador Técnico", prazo: "D+15", observacao: "98,7% da área coberta" },
  { id: "lb-08", descricao: "Conferência do SRID", concluido: true, responsavel: "Analista GIS", prazo: "D+2", observacao: "SRID 31983 / SIRGAS 2000" },
  { id: "lb-09", descricao: "Conferência da qualidade das imagens", concluido: true, responsavel: "Processamento", prazo: "D+6", observacao: "Nenhuma imagem com blur" },
];

export const baseSurveys: BaseSurvey[] = [
  { id: "bs-01", nome: "Drone RGB - Setor Central", tipo: "drone_rgb", data: "12/03/2026", equipamento: "DJI Phantom 4 RTK", areaCoberta: "4,8 km²", status: "Processado", qualidade: 96, arvoresPreIdentificadas: 3124 },
  { id: "bs-02", nome: "Drone RGB - Setor Sul", tipo: "drone_rgb", data: "13/03/2026", equipamento: "DJI Phantom 4 RTK", areaCoberta: "5,1 km²", status: "Processado", qualidade: 94, arvoresPreIdentificadas: 2891 },
  { id: "bs-03", nome: "Drone LiDAR - Corredor Principal", tipo: "drone_lidar", data: "18/03/2026", equipamento: "Matrice 300 + L1", areaCoberta: "6,2 km²", status: "Em processamento", qualidade: 88, arvoresPreIdentificadas: 0 },
  { id: "bs-04", nome: "Laser Scanner - Praças", tipo: "laser_scanner", data: "20/03/2026", equipamento: "FARO Focus S350", areaCoberta: "0,8 km²", status: "Aguardando manutenção", qualidade: 0, arvoresPreIdentificadas: 0 },
  { id: "bs-05", nome: "Ortofoto Municipal 2025", tipo: "ortofoto", data: "05/01/2026", equipamento: "VANT Asa Fixa", areaCoberta: "42 km²", status: "Disponível", qualidade: 92, arvoresPreIdentificadas: 10842 },
  { id: "bs-06", nome: "GNSS - Pontos de Controle", tipo: "gnss", data: "10/03/2026", equipamento: "Trimble R2", areaCoberta: "18 pontos", status: "Validado", qualidade: 99, arvoresPreIdentificadas: 0 },
  { id: "bs-07", nome: "Base Cartográfica Municipal", tipo: "base_cartografica", data: "01/02/2026", equipamento: "Prefeitura Municipal", areaCoberta: "42 km²", status: "Disponível", qualidade: 85, arvoresPreIdentificadas: 0 },
];

export const fieldProduction: FieldProduction[] = [
  { tecnico: "Carlos Silva", equipe: "Equipe Alfa", bairro: "Setor Central", arvoresCadastradas: 1842, vistorias: 1842, fotos: 5536, pendencias: 12, ultimaSincronizacao: "15/06 16:42", status: "Ativo" },
  { tecnico: "Ana Costa", equipe: "Equipe Alfa", bairro: "Setor Central", arvoresCadastradas: 1628, vistorias: 1628, fotos: 4890, pendencias: 5, ultimaSincronizacao: "15/06 17:15", status: "Ativo" },
  { tecnico: "Pedro Santos", equipe: "Equipe Beta", bairro: "Setor Sul", arvoresCadastradas: 2115, vistorias: 2115, fotos: 6350, pendencias: 8, ultimaSincronizacao: "15/06 16:58", status: "Ativo" },
  { tecnico: "Marina Oliveira", equipe: "Equipe Beta", bairro: "Setor Sul", arvoresCadastradas: 1903, vistorias: 1903, fotos: 5712, pendencias: 3, ultimaSincronizacao: "15/06 17:30", status: "Ativo" },
  { tecnico: "João Mendes", equipe: "Equipe Gama", bairro: "Setor Norte", arvoresCadastradas: 1432, vistorias: 1432, fotos: 4298, pendencias: 18, ultimaSincronizacao: "14/06 18:00", status: "Pendente" },
  { tecnico: "Laura Freitas", equipe: "Equipe Gama", bairro: "Praças e Parques", arvoresCadastradas: 987, vistorias: 987, fotos: 2965, pendencias: 2, ultimaSincronizacao: "15/06 16:20", status: "Ativo" },
];

export const processing3DData: Processing3D[] = [
  { id: "p3d-01", levantamento: "Drone RGB - Setor Central", status: "Processado", pontosProcessados: 184200000, arvoresVinculadas: 3124, alturasExtraidas: 2891, copasEstimadas: 2745, interferenciasDetectadas: 156, qualidade: 94 },
  { id: "p3d-02", levantamento: "Drone RGB - Setor Sul", status: "Processado", pontosProcessados: 201500000, arvoresVinculadas: 2891, alturasExtraidas: 2678, copasEstimadas: 2540, interferenciasDetectadas: 132, qualidade: 92 },
  { id: "p3d-03", levantamento: "Drone LiDAR - Corredor Principal", status: "Em processamento", pontosProcessados: 89200000, arvoresVinculadas: 1245, alturasExtraidas: 1102, copasEstimadas: 980, interferenciasDetectadas: 89, qualidade: 85 },
  { id: "p3d-04", levantamento: "Laser Scanner - Praças", status: "Aguardando processamento", pontosProcessados: 0, arvoresVinculadas: 0, alturasExtraidas: 0, copasEstimadas: 0, interferenciasDetectadas: 0, qualidade: 0 },
  { id: "p3d-05", levantamento: "Ortofoto Municipal 2025", status: "Validado", pontosProcessados: 0, arvoresVinculadas: 8942, alturasExtraidas: 6930, copasEstimadas: 5716, interferenciasDetectadas: 438, qualidade: 88 },
];

export const ladmCompleteness: LadmCompleteness = {
  arvoresComUnidadeEspacial: 10842,
  arvoresComFonte: 9764,
  arvoresComResponsavel: 8936,
  arvoresComRRR: 7812,
  arvoresComHistorico: 9245,
  arvoresComDocumentos: 8654,
  arvoresComNuvemPontos: 7842,
  totalArvores: 12486,
};

export const ladmModelingData = [
  { codigoCTA: "CTA-2026-0001423", unidadeEspacial: "Praça Universitária", fonte: "Vistoria Técnica", responsavel: "Carlos Silva", rrr: "Autorização Poda", historico: "3 eventos", status: "Completo" },
  { codigoCTA: "CTA-2026-0001456", unidadeEspacial: "Rua 10 - Centro", fonte: "Foto Georreferenciada", responsavel: "Ana Costa", rrr: "Pendente", historico: "1 evento", status: "Incompleto" },
  { codigoCTA: "CTA-2026-0001498", unidadeEspacial: "Canteiro Av. Goiás", fonte: "Nuvem de Pontos", responsavel: "Pedro Santos", rrr: "Restrição APP", historico: "5 eventos", status: "Completo" },
  { codigoCTA: "CTA-2026-0001521", unidadeEspacial: "Parque Ambiental", fonte: "Laudo Técnico", responsavel: "Marina Oliveira", rrr: "Autorização Supressão", historico: "2 eventos", status: "Completo" },
  { codigoCTA: "CTA-2026-0001550", unidadeEspacial: "Pendente", fonte: "Pendente", responsavel: "Pendente", rrr: "Pendente", historico: "0 eventos", status: "Crítico" },
  { codigoCTA: "CTA-2026-0001603", unidadeEspacial: "Rua 15 - Sul", fonte: "Ortofoto Drone", responsavel: "João Mendes", rrr: "Nenhum", historico: "1 evento", status: "Pendente" },
  { codigoCTA: "CTA-2026-0001645", unidadeEspacial: "Lote 42 - Setor Norte", fonte: "Coleta de Campo", responsavel: "Laura Freitas", rrr: "Obrigação Manutenção", historico: "4 eventos", status: "Completo" },
];

export const validationItems: ValidationItem[] = [
  { id: "val-01", regra: "Árvore possui geometria", totalAvaliado: 12486, conformes: 12184, inconformes: 302, severidade: "Crítica", status: "Em validação", acaoRecomendada: "Corrigir coordenadas ausentes" },
  { id: "val-02", regra: "Árvore vinculada à unidade espacial", totalAvaliado: 12486, conformes: 10842, inconformes: 1644, severidade: "Alta", status: "Em validação", acaoRecomendada: "Vincular árvores ao território" },
  { id: "val-03", regra: "Árvore possui data de cadastro", totalAvaliado: 12486, conformes: 12486, inconformes: 0, severidade: "Média", status: "Conforme", acaoRecomendada: "Nenhuma" },
  { id: "val-04", regra: "Alteração gera histórico", totalAvaliado: 14820, conformes: 13982, inconformes: 838, severidade: "Alta", status: "Em validação", acaoRecomendada: "Versionar eventos pendentes" },
  { id: "val-05", regra: "Informação técnica possui fonte", totalAvaliado: 12486, conformes: 9764, inconformes: 2722, severidade: "Crítica", status: "Em validação", acaoRecomendada: "Vincular fontes documentais" },
  { id: "val-06", regra: "Supressão possui autorização", totalAvaliado: 312, conformes: 284, inconformes: 28, severidade: "Crítica", status: "Pendente", acaoRecomendada: "Regularizar autorizações" },
  { id: "val-07", regra: "Risco alto possui evidência fotográfica", totalAvaliado: 486, conformes: 438, inconformes: 48, severidade: "Crítica", status: "Pendente", acaoRecomendada: "Adicionar fotos de risco" },
  { id: "val-08", regra: "Nuvem de pontos possui metadados", totalAvaliado: 24, conformes: 18, inconformes: 6, severidade: "Alta", status: "Em validação", acaoRecomendada: "Completar metadados LAS/LAZ" },
  { id: "val-09", regra: "Intervenção possui responsável", totalAvaliado: 1846, conformes: 1634, inconformes: 212, severidade: "Alta", status: "Em validação", acaoRecomendada: "Atribuir responsáveis" },
  { id: "val-10", regra: "Árvore removida no histórico", totalAvaliado: 312, conformes: 298, inconformes: 14, severidade: "Média", status: "Em validação", acaoRecomendada: "Registrar remoções" },
];

export const validationAlertCards = [
  { titulo: "Qualidade Geral", valor: "87%", icon: "ri-check-double-line", variant: "success" as const, descricao: "Acima da meta de 85%" },
  { titulo: "Inconsistências Críticas", valor: "126", icon: "ri-error-warning-line", variant: "danger" as const, descricao: "Precisam de atenção imediata" },
  { titulo: "Pendências Moderadas", valor: "438", icon: "ri-alert-line", variant: "warning" as const, descricao: "Resolver em até 15 dias" },
  { titulo: "Duplicidades", valor: "74", icon: "ri-file-copy-line", variant: "warning" as const, descricao: "Possíveis árvores duplicadas" },
  { titulo: "Sem Geometria", valor: "302", icon: "ri-map-pin-line", variant: "danger" as const, descricao: "Coordenadas pendentes" },
  { titulo: "Risco Alto sem Foto", valor: "48", icon: "ri-camera-off-line", variant: "danger" as const, descricao: "Evidência fotográfica obrigatória" },
  { titulo: "Supressão sem Autorização", valor: "28", icon: "ri-file-warning-line", variant: "danger" as const, descricao: "Documento obrigatório ausente" },
  { titulo: "Nuvem sem Metadados", valor: "6", icon: "ri-stack-line", variant: "warning" as const, descricao: "Completar metadados LAS/LAZ" },
];

export const publicationProducts: PublicationProduct[] = [
  { id: "pp-01", produto: "Mapa Arbóreo Interativo", tipo: "Mapa Web", status: "Publicado", visibilidade: "publico", ultimaGeracao: "14/06/2026", responsavel: "Analista GIS" },
  { id: "pp-02", produto: "Dashboard Gerencial", tipo: "Dashboard", status: "Publicado", visibilidade: "privado", ultimaGeracao: "13/06/2026", responsavel: "Coordenador Técnico" },
  { id: "pp-03", produto: "Relatório Técnico Final", tipo: "Relatório PDF", status: "Em revisão", visibilidade: "privado", ultimaGeracao: "10/06/2026", responsavel: "Engenheiro Florestal" },
  { id: "pp-04", produto: "Mapa de Risco", tipo: "Mapa Temático", status: "Gerado", visibilidade: "privado", ultimaGeracao: "08/06/2026", responsavel: "Analista GIS" },
  { id: "pp-05", produto: "Mapa de Poda", tipo: "Mapa Temático", status: "Gerado", visibilidade: "privado", ultimaGeracao: "08/06/2026", responsavel: "Coordenador Técnico" },
  { id: "pp-06", produto: "Mapa de Espécies", tipo: "Mapa Temático", status: "Publicado", visibilidade: "publico", ultimaGeracao: "05/06/2026", responsavel: "Analista GIS" },
  { id: "pp-07", produto: "Base GeoJSON", tipo: "Dados GIS", status: "Publicado", visibilidade: "publico", ultimaGeracao: "12/06/2026", responsavel: "Analista GIS" },
  { id: "pp-08", produto: "Portal Público", tipo: "Portal Web", status: "Em geração", visibilidade: "publico", ultimaGeracao: "-", responsavel: "Desenvolvedor" },
  { id: "pp-09", produto: "Relatório Fotográfico", tipo: "Relatório PDF", status: "Em revisão", visibilidade: "privado", ultimaGeracao: "09/06/2026", responsavel: "Técnico de Campo" },
  { id: "pp-10", produto: "Mapa de Cobertura Vegetal", tipo: "Mapa Temático", status: "Gerado", visibilidade: "publico", ultimaGeracao: "07/06/2026", responsavel: "Analista GIS" },
  { id: "pp-11", produto: "Plano Preliminar de Manejo", tipo: "Documento Técnico", status: "Não gerado", visibilidade: "privado", ultimaGeracao: "-", responsavel: "Engenheiro Florestal" },
  { id: "pp-12", produto: "Mapa de Supressão", tipo: "Mapa Temático", status: "Gerado", visibilidade: "privado", ultimaGeracao: "06/06/2026", responsavel: "Coordenador Técnico" },
];

export const deliverables: Deliverable[] = [
  { id: "del-01", titulo: "Base Cadastral Arbórea Georreferenciada", tipo: "Dados GIS", descricao: "Shapefile + GeoJSON com todas as árvores cadastradas, atributos completos e metadados", status: "Gerado", formato: "SHP/GeoJSON", ultimaAtualizacao: "12/06/2026" },
  { id: "del-02", titulo: "Mapa das Árvores Cadastradas", tipo: "Mapa", descricao: "Mapa interativo web com localização de todas as árvores, filtros por espécie, risco e manejo", status: "Gerado", formato: "Web Map", ultimaAtualizacao: "14/06/2026" },
  { id: "del-03", titulo: "Mapa de Risco", tipo: "Mapa Temático", descricao: "Mapa categorizando árvores por nível de risco: baixo, médio, alto e crítico", status: "Gerado", formato: "Web Map + PDF", ultimaAtualizacao: "08/06/2026" },
  { id: "del-04", titulo: "Mapa de Poda", tipo: "Mapa Temático", descricao: "Mapa com recomendações de poda: leve, moderada, drástica e emergencial", status: "Gerado", formato: "Web Map", ultimaAtualizacao: "08/06/2026" },
  { id: "del-05", titulo: "Mapa de Supressão", tipo: "Mapa Temático", descricao: "Mapa indicando árvores com recomendação de supressão e respectivas autorizações", status: "Gerado", formato: "PDF", ultimaAtualizacao: "06/06/2026" },
  { id: "del-06", titulo: "Mapa de Cobertura Vegetal", tipo: "Mapa Temático", descricao: "Mapa de cobertura vegetal urbana com classificação por densidade e tipo", status: "Gerado", formato: "Web Map + PDF", ultimaAtualizacao: "07/06/2026" },
  { id: "del-07", titulo: "Mapa de Espécies", tipo: "Mapa Temático", descricao: "Mapa com distribuição das espécies arbóreas por região da cidade", status: "Gerado", formato: "Web Map", ultimaAtualizacao: "05/06/2026" },
  { id: "del-08", titulo: "Relatório por Bairro", tipo: "Relatório", descricao: "Relatório detalhado com estatísticas arbóreas segmentadas por bairro", status: "Em geração", formato: "PDF", ultimaAtualizacao: "-" },
  { id: "del-09", titulo: "Relatório por Logradouro", tipo: "Relatório", descricao: "Relatório com análise detalhada da arborização por logradouro", status: "Pendente", formato: "PDF", ultimaAtualizacao: "-" },
  { id: "del-10", titulo: "Relatório de Responsabilidades", tipo: "Relatório", descricao: "Relatório consolidando todas as responsabilidades, RRR e partes envolvidas", status: "Gerado", formato: "PDF", ultimaAtualizacao: "10/06/2026" },
  { id: "del-11", titulo: "Relatório de Restrições Ambientais", tipo: "Relatório", descricao: "Relatório de restrições ambientais: APP, UC, espécies protegidas e áreas tombadas", status: "Gerado", formato: "PDF", ultimaAtualizacao: "09/06/2026" },
  { id: "del-12", titulo: "Relatório de Histórico de Manejo", tipo: "Relatório", descricao: "Relatório com timeline de todos os eventos de manejo registrados", status: "Em geração", formato: "PDF", ultimaAtualizacao: "-" },
  { id: "del-13", titulo: "Relatório Técnico com Fontes", tipo: "Relatório", descricao: "Relatório técnico completo com todas as fontes documentais e evidências", status: "Gerado", formato: "PDF", ultimaAtualizacao: "11/06/2026" },
  { id: "del-14", titulo: "Dashboard Gerencial", tipo: "Dashboard", descricao: "Dashboard interativo com KPIs, gráficos, mapas e indicadores da ação", status: "Gerado", formato: "Web App", ultimaAtualizacao: "13/06/2026" },
  { id: "del-15", titulo: "Banco de Imagens", tipo: "Acervo", descricao: "Banco de imagens georreferenciadas de todas as árvores cadastradas", status: "Gerado", formato: "JPG + Metadados", ultimaAtualizacao: "15/06/2026" },
  { id: "del-16", titulo: "Nuvem de Pontos Processada", tipo: "Dados 3D", descricao: "Nuvem de pontos classificada (solo, vegetação, edificações) em formato LAS/LAZ", status: "Gerado", formato: "LAS/LAZ", ultimaAtualizacao: "08/06/2026" },
  { id: "del-17", titulo: "Modelo 3D da Arborização", tipo: "Modelo 3D", descricao: "Modelo 3D interativo da arborização com alturas, copas e métricas extraídas", status: "Em geração", formato: "WebGL/3D Tiles", ultimaAtualizacao: "-" },
  { id: "del-18", titulo: "Plano Preliminar de Manejo", tipo: "Documento", descricao: "Plano com recomendações de manejo priorizadas por risco e urgência", status: "Pendente", formato: "PDF", ultimaAtualizacao: "-" },
];

export interface OperationalReport {
  id: string;
  titulo: string;
  descricao: string;
  etapaRelacionada: string;
  ultimaGeracao: string;
  status: string;
}

export const operationalReports: OperationalReport[] = [
  { id: "opr-01", titulo: "Plano Operacional da Ação", descricao: "Documento com escopo, cronograma, equipe, equipamentos e metodologia da ação", etapaRelacionada: "Planejamento", ultimaGeracao: "01/03/2026", status: "Gerado" },
  { id: "opr-02", titulo: "Relatório de Planejamento", descricao: "Detalhamento do planejamento: área, unidades espaciais, critérios técnicos e logística", etapaRelacionada: "Planejamento", ultimaGeracao: "05/03/2026", status: "Gerado" },
  { id: "opr-03", titulo: "Relatório de Levantamento Base", descricao: "Resultados do drone, ortomosaico, LiDAR e pré-identificação de árvores", etapaRelacionada: "Levantamento Base", ultimaGeracao: "20/03/2026", status: "Gerado" },
  { id: "opr-04", titulo: "Relatório de Produção de Campo", descricao: "Produtividade das equipes, árvores cadastradas, fotos, vistorias e pendências", etapaRelacionada: "Cadastro de Campo", ultimaGeracao: "15/06/2026", status: "Gerado" },
  { id: "opr-05", titulo: "Relatório de Processamento 3D", descricao: "Nuvens processadas, métricas extraídas, classificação e interferências detectadas", etapaRelacionada: "Processamento 3D", ultimaGeracao: "10/06/2026", status: "Em geração" },
  { id: "opr-06", titulo: "Relatório de Modelagem LADM", descricao: "Completude da modelagem: unidades espaciais, fontes, RRR e histórico", etapaRelacionada: "Modelagem LADM", ultimaGeracao: "12/06/2026", status: "Em geração" },
  { id: "opr-07", titulo: "Relatório de Validação", descricao: "Resultados das validações: regras, conformidades, inconsistências e correções", etapaRelacionada: "Validação", ultimaGeracao: "14/06/2026", status: "Gerado" },
  { id: "opr-08", titulo: "Relatório de Publicação", descricao: "Produtos publicados, mapas, dashboards, portais e camadas disponibilizadas", etapaRelacionada: "Publicação e Gestão", ultimaGeracao: "15/06/2026", status: "Em revisão" },
  { id: "opr-09", titulo: "Relatório Final da Ação", descricao: "Relatório consolidado com resumo executivo, resultados, indicadores e lições aprendidas", etapaRelacionada: "Publicação e Gestão", ultimaGeracao: "-", status: "Pendente" },
  { id: "opr-10", titulo: "Relatório de Indicadores de Gestão", descricao: "Indicadores para gestão municipal: cobertura arbórea, risco, manejo e evolução", etapaRelacionada: "Publicação e Gestão", ultimaGeracao: "-", status: "Pendente" },
];

export const timelineStages: StageInfo[] = [
  { id: 1, nome: "Planejamento", status: "concluida", progresso: 100, responsavel: "Coord. Técnico", dataPrevista: "01/03 a 31/03", dataConcluida: "28/03/2026", pendencias: 0, documentosVinculados: 3 },
  { id: 2, nome: "Levantamento Base", status: "em_revisao", progresso: 78, responsavel: "Piloto Drone", dataPrevista: "01/04 a 20/04", dataConcluida: null, pendencias: 2, documentosVinculados: 7 },
  { id: 3, nome: "Cadastro de Campo", status: "em_andamento", progresso: 65, responsavel: "Coord. Campo", dataPrevista: "15/04 a 30/06", dataConcluida: null, pendencias: 5, documentosVinculados: 4 },
  { id: 4, nome: "Processamento 3D", status: "em_andamento", progresso: 52, responsavel: "Processamento", dataPrevista: "01/05 a 15/07", dataConcluida: null, pendencias: 3, documentosVinculados: 5 },
  { id: 5, nome: "Modelagem LADM", status: "em_andamento", progresso: 44, responsavel: "Analista Dados", dataPrevista: "15/05 a 31/07", dataConcluida: null, pendencias: 4, documentosVinculados: 2 },
  { id: 6, nome: "Validação", status: "planejada", progresso: 0, responsavel: "Coord. Técnico", dataPrevista: "01/07 a 15/08", dataConcluida: null, pendencias: 0, documentosVinculados: 0 },
  { id: 7, nome: "Publicação e Gestão", status: "nao_iniciada", progresso: 0, responsavel: "Gerente Projeto", dataPrevista: "15/08 a 30/08", dataConcluida: null, pendencias: 0, documentosVinculados: 0 },
];

export const cadastralActions: CadastralAction[] = [
  {
    id: "acao-01",
    nome: "CTA Goiânia Centro",
    municipio: "Goiânia",
    uf: "GO",
    projetoVinculado: "CTA Municipal Goiânia - Fase 1",
    cliente: "Prefeitura Municipal de Goiânia",
    areaEstudo: "Setor Central - 4,8 km²",
    tipoAcao: "Implantação do CTA Municipal",
    etapaAtual: "Cadastro de Campo",
    progresso: 65,
    arvoresPrevistas: 8500,
    arvoresCadastradas: 5528,
    responsavel: "Dr. Roberto Almeida",
    equipe: ["Carlos Silva", "Ana Costa", "Pedro Santos", "Marina Oliveira", "João Mendes"],
    status: "em_andamento",
    pendencias: 5,
    dataInicio: "01/03/2026",
    dataPrevistaConclusao: "30/08/2026",
    equipamentos: equipmentDefaults,
    stages: timelineStages,
  },
  {
    id: "acao-02",
    nome: "Inventário Praça Universitária",
    municipio: "Goiânia",
    uf: "GO",
    projetoVinculado: "Praças e Parques Urbanos",
    cliente: "Secretaria Municipal de Meio Ambiente",
    areaEstudo: "Praça Universitária - 0,24 km²",
    tipoAcao: "Inventário de Praça",
    etapaAtual: "Validação",
    progresso: 88,
    arvoresPrevistas: 420,
    arvoresCadastradas: 418,
    responsavel: "Eng. Flávia Mendonça",
    equipe: ["Carlos Silva", "Ana Costa"],
    status: "em_validacao",
    pendencias: 2,
    dataInicio: "15/01/2026",
    dataPrevistaConclusao: "30/04/2026",
    equipamentos: equipmentDefaults.slice(0, 6),
    stages: [
      { id: 1, nome: "Planejamento", status: "concluida", progresso: 100, responsavel: "Eng. Flávia Mendonça", dataPrevista: "15/01 a 25/01", dataConcluida: "24/01/2026", pendencias: 0, documentosVinculados: 2 },
      { id: 2, nome: "Levantamento Base", status: "concluida", progresso: 100, responsavel: "Piloto Drone", dataPrevista: "26/01 a 05/02", dataConcluida: "04/02/2026", pendencias: 0, documentosVinculados: 3 },
      { id: 3, nome: "Cadastro de Campo", status: "concluida", progresso: 100, responsavel: "Carlos Silva", dataPrevista: "06/02 a 20/02", dataConcluida: "18/02/2026", pendencias: 0, documentosVinculados: 1 },
      { id: 4, nome: "Processamento 3D", status: "concluida", progresso: 100, responsavel: "Processamento", dataPrevista: "21/02 a 05/03", dataConcluida: "03/03/2026", pendencias: 0, documentosVinculados: 2 },
      { id: 5, nome: "Modelagem LADM", status: "concluida", progresso: 100, responsavel: "Analista Dados", dataPrevista: "06/03 a 20/03", dataConcluida: "19/03/2026", pendencias: 0, documentosVinculados: 1 },
      { id: 6, nome: "Validação", status: "em_andamento", progresso: 70, responsavel: "Eng. Flávia Mendonça", dataPrevista: "21/03 a 10/04", dataConcluida: null, pendencias: 2, documentosVinculados: 0 },
      { id: 7, nome: "Publicação e Gestão", status: "planejada", progresso: 20, responsavel: "Analista GIS", dataPrevista: "11/04 a 30/04", dataConcluida: null, pendencias: 0, documentosVinculados: 0 },
    ],
  },
  {
    id: "acao-03",
    nome: "Parque Ambiental Norte",
    municipio: "Goiânia",
    uf: "GO",
    projetoVinculado: "UC Parque Natural Municipal",
    cliente: "Prefeitura Municipal de Goiânia",
    areaEstudo: "Parque Ambiental Norte - 1,8 km²",
    tipoAcao: "Inventário de UC",
    etapaAtual: "Cadastro de Campo",
    progresso: 42,
    arvoresPrevistas: 3200,
    arvoresCadastradas: 1344,
    responsavel: "Biól. Helena Torres",
    equipe: ["Pedro Santos", "Marina Oliveira", "Laura Freitas"],
    status: "em_andamento",
    pendencias: 8,
    dataInicio: "01/04/2026",
    dataPrevistaConclusao: "30/09/2026",
    equipamentos: equipmentDefaults,
    stages: [
      { id: 1, nome: "Planejamento", status: "concluida", progresso: 100, responsavel: "Biól. Helena Torres", dataPrevista: "01/04 a 15/04", dataConcluida: "14/04/2026", pendencias: 0, documentosVinculados: 2 },
      { id: 2, nome: "Levantamento Base", status: "concluida", progresso: 100, responsavel: "Piloto Drone", dataPrevista: "16/04 a 30/04", dataConcluida: "28/04/2026", pendencias: 0, documentosVinculados: 4 },
      { id: 3, nome: "Cadastro de Campo", status: "em_andamento", progresso: 38, responsavel: "Coord. Campo", dataPrevista: "01/05 a 31/07", dataConcluida: null, pendencias: 5, documentosVinculados: 0 },
      { id: 4, nome: "Processamento 3D", status: "planejada", progresso: 0, responsavel: "Processamento", dataPrevista: "01/08 a 31/08", dataConcluida: null, pendencias: 0, documentosVinculados: 0 },
      { id: 5, nome: "Modelagem LADM", status: "nao_iniciada", progresso: 0, responsavel: "Analista Dados", dataPrevista: "01/09 a 15/09", dataConcluida: null, pendencias: 0, documentosVinculados: 0 },
      { id: 6, nome: "Validação", status: "nao_iniciada", progresso: 0, responsavel: "Biól. Helena Torres", dataPrevista: "16/09 a 25/09", dataConcluida: null, pendencias: 0, documentosVinculados: 0 },
      { id: 7, nome: "Publicação e Gestão", status: "nao_iniciada", progresso: 0, responsavel: "Analista GIS", dataPrevista: "26/09 a 30/09", dataConcluida: null, pendencias: 0, documentosVinculados: 0 },
    ],
  },
  {
    id: "acao-04",
    nome: "Corredor Verde Sul",
    municipio: "Goiânia",
    uf: "GO",
    projetoVinculado: "Corredores Verdes Urbanos",
    cliente: "Secretaria de Planejamento Urbano",
    areaEstudo: "Av. 85 até Av. T-63 - 3,2 km lineares",
    tipoAcao: "Inventário Arbóreo Urbano",
    etapaAtual: "Levantamento Base",
    progresso: 28,
    arvoresPrevistas: 1800,
    arvoresCadastradas: 0,
    responsavel: "Arq. Ricardo Neves",
    equipe: ["Carlos Silva", "Ana Costa"],
    status: "em_andamento",
    pendencias: 3,
    dataInicio: "01/06/2026",
    dataPrevistaConclusao: "15/10/2026",
    equipamentos: equipmentDefaults.slice(0, 7),
    stages: [
      { id: 1, nome: "Planejamento", status: "concluida", progresso: 100, responsavel: "Arq. Ricardo Neves", dataPrevista: "01/06 a 10/06", dataConcluida: "09/06/2026", pendencias: 0, documentosVinculados: 1 },
      { id: 2, nome: "Levantamento Base", status: "em_andamento", progresso: 45, responsavel: "Piloto Drone", dataPrevista: "11/06 a 30/06", dataConcluida: null, pendencias: 2, documentosVinculados: 2 },
      { id: 3, nome: "Cadastro de Campo", status: "planejada", progresso: 0, responsavel: "Coord. Campo", dataPrevista: "01/07 a 15/08", dataConcluida: null, pendencias: 0, documentosVinculados: 0 },
      { id: 4, nome: "Processamento 3D", status: "nao_iniciada", progresso: 0, responsavel: "Processamento", dataPrevista: "16/08 a 31/08", dataConcluida: null, pendencias: 0, documentosVinculados: 0 },
      { id: 5, nome: "Modelagem LADM", status: "nao_iniciada", progresso: 0, responsavel: "Analista Dados", dataPrevista: "01/09 a 15/09", dataConcluida: null, pendencias: 0, documentosVinculados: 0 },
      { id: 6, nome: "Validação", status: "nao_iniciada", progresso: 0, responsavel: "Arq. Ricardo Neves", dataPrevista: "16/09 a 30/09", dataConcluida: null, pendencias: 0, documentosVinculados: 0 },
      { id: 7, nome: "Publicação e Gestão", status: "nao_iniciada", progresso: 0, responsavel: "Analista GIS", dataPrevista: "01/10 a 15/10", dataConcluida: null, pendencias: 0, documentosVinculados: 0 },
    ],
  },
  {
    id: "acao-05",
    nome: "Árvores Críticas Setor Bueno",
    municipio: "Goiânia",
    uf: "GO",
    projetoVinculado: "Monitoramento de Árvores de Risco",
    cliente: "Defesa Civil Municipal",
    areaEstudo: "Setor Bueno - 2,1 km²",
    tipoAcao: "Monitoramento de Árvores de Risco",
    etapaAtual: "Validação",
    progresso: 90,
    arvoresPrevistas: 486,
    arvoresCadastradas: 486,
    responsavel: "Eng. Flávia Mendonça",
    equipe: ["Carlos Silva", "Ana Costa", "Pedro Santos"],
    status: "em_validacao",
    pendencias: 1,
    dataInicio: "01/02/2026",
    dataPrevistaConclusao: "15/07/2026",
    equipamentos: equipmentDefaults.slice(0, 8),
    stages: [
      { id: 1, nome: "Planejamento", status: "concluida", progresso: 100, responsavel: "Eng. Flávia Mendonça", dataPrevista: "01/02 a 10/02", dataConcluida: "09/02/2026", pendencias: 0, documentosVinculados: 1 },
      { id: 2, nome: "Levantamento Base", status: "concluida", progresso: 100, responsavel: "Piloto Drone", dataPrevista: "11/02 a 20/02", dataConcluida: "19/02/2026", pendencias: 0, documentosVinculados: 2 },
      { id: 3, nome: "Cadastro de Campo", status: "concluida", progresso: 100, responsavel: "Carlos Silva", dataPrevista: "21/02 a 15/03", dataConcluida: "14/03/2026", pendencias: 0, documentosVinculados: 2 },
      { id: 4, nome: "Processamento 3D", status: "concluida", progresso: 100, responsavel: "Processamento", dataPrevista: "16/03 a 31/03", dataConcluida: "30/03/2026", pendencias: 0, documentosVinculados: 3 },
      { id: 5, nome: "Modelagem LADM", status: "concluida", progresso: 100, responsavel: "Analista Dados", dataPrevista: "01/04 a 15/04", dataConcluida: "14/04/2026", pendencias: 0, documentosVinculados: 1 },
      { id: 6, nome: "Validação", status: "em_andamento", progresso: 82, responsavel: "Eng. Flávia Mendonça", dataPrevista: "16/04 a 15/05", dataConcluida: null, pendencias: 1, documentosVinculados: 0 },
      { id: 7, nome: "Publicação e Gestão", status: "planejada", progresso: 10, responsavel: "Defesa Civil", dataPrevista: "16/05 a 15/07", dataConcluida: null, pendencias: 0, documentosVinculados: 0 },
    ],
  },
  {
    id: "acao-06",
    nome: "Compensação Ambiental Vila Nova",
    municipio: "Goiânia",
    uf: "GO",
    projetoVinculado: "Compensação Ambiental - Empreendimento Vila Nova",
    cliente: "Construtora Vila Nova Ltda",
    areaEstudo: "Loteamento Vila Nova - 0,6 km²",
    tipoAcao: "Compensação Ambiental",
    etapaAtual: "Cadastro de Campo",
    progresso: 48,
    arvoresPrevistas: 240,
    arvoresCadastradas: 115,
    responsavel: "Eng. Paulo Ribeiro",
    equipe: ["João Mendes", "Laura Freitas"],
    status: "em_andamento",
    pendencias: 4,
    dataInicio: "15/05/2026",
    dataPrevistaConclusao: "30/09/2026",
    equipamentos: equipmentDefaults.slice(0, 6),
    stages: [
      { id: 1, nome: "Planejamento", status: "concluida", progresso: 100, responsavel: "Eng. Paulo Ribeiro", dataPrevista: "15/05 a 25/05", dataConcluida: "24/05/2026", pendencias: 0, documentosVinculados: 2 },
      { id: 2, nome: "Levantamento Base", status: "concluida", progresso: 100, responsavel: "Piloto Drone", dataPrevista: "26/05 a 05/06", dataConcluida: "04/06/2026", pendencias: 0, documentosVinculados: 3 },
      { id: 3, nome: "Cadastro de Campo", status: "em_andamento", progresso: 45, responsavel: "João Mendes", dataPrevista: "06/06 a 30/07", dataConcluida: null, pendencias: 2, documentosVinculados: 0 },
      { id: 4, nome: "Processamento 3D", status: "planejada", progresso: 0, responsavel: "Processamento", dataPrevista: "01/08 a 15/08", dataConcluida: null, pendencias: 0, documentosVinculados: 0 },
      { id: 5, nome: "Modelagem LADM", status: "nao_iniciada", progresso: 0, responsavel: "Analista Dados", dataPrevista: "16/08 a 31/08", dataConcluida: null, pendencias: 0, documentosVinculados: 0 },
      { id: 6, nome: "Validação", status: "nao_iniciada", progresso: 0, responsavel: "Eng. Paulo Ribeiro", dataPrevista: "01/09 a 15/09", dataConcluida: null, pendencias: 0, documentosVinculados: 0 },
      { id: 7, nome: "Publicação e Gestão", status: "nao_iniciada", progresso: 0, responsavel: "Analista GIS", dataPrevista: "16/09 a 30/09", dataConcluida: null, pendencias: 0, documentosVinculados: 0 },
    ],
  },
  {
    id: "acao-07",
    nome: "Levantamento 3D Carbono Urbano",
    municipio: "Goiânia",
    uf: "GO",
    projetoVinculado: "Carbono Urbano Goiânia",
    cliente: "Secretaria Municipal de Meio Ambiente",
    areaEstudo: "Toda área urbana - 42 km²",
    tipoAcao: "Projeto de Carbono Urbano",
    etapaAtual: "Processamento 3D",
    progresso: 35,
    arvoresPrevistas: 25000,
    arvoresCadastradas: 8942,
    responsavel: "Dr. Roberto Almeida",
    equipe: ["Carlos Silva", "Ana Costa", "Pedro Santos", "Marina Oliveira", "João Mendes", "Laura Freitas"],
    status: "bloqueada",
    pendencias: 6,
    dataInicio: "01/01/2026",
    dataPrevistaConclusao: "31/12/2026",
    equipamentos: equipmentDefaults,
    stages: [
      { id: 1, nome: "Planejamento", status: "concluida", progresso: 100, responsavel: "Dr. Roberto Almeida", dataPrevista: "01/01 a 31/01", dataConcluida: "29/01/2026", pendencias: 0, documentosVinculados: 4 },
      { id: 2, nome: "Levantamento Base", status: "concluida", progresso: 100, responsavel: "Piloto Drone", dataPrevista: "01/02 a 28/02", dataConcluida: "26/02/2026", pendencias: 0, documentosVinculados: 5 },
      { id: 3, nome: "Cadastro de Campo", status: "concluida", progresso: 100, responsavel: "Coord. Campo", dataPrevista: "01/03 a 31/05", dataConcluida: "30/05/2026", pendencias: 0, documentosVinculados: 3 },
      { id: 4, nome: "Processamento 3D", status: "bloqueada", progresso: 42, responsavel: "Processamento", dataPrevista: "01/06 a 31/07", dataConcluida: null, pendencias: 4, documentosVinculados: 2 },
      { id: 5, nome: "Modelagem LADM", status: "nao_iniciada", progresso: 0, responsavel: "Analista Dados", dataPrevista: "01/08 a 15/09", dataConcluida: null, pendencias: 0, documentosVinculados: 0 },
      { id: 6, nome: "Validação", status: "nao_iniciada", progresso: 0, responsavel: "Dr. Roberto Almeida", dataPrevista: "16/09 a 30/10", dataConcluida: null, pendencias: 0, documentosVinculados: 0 },
      { id: 7, nome: "Publicação e Gestão", status: "nao_iniciada", progresso: 0, responsavel: "Analista GIS", dataPrevista: "01/11 a 31/12", dataConcluida: null, pendencias: 0, documentosVinculados: 0 },
    ],
  },
];

export const blockingIssues = [
  { id: "bl-01", acao: "Levantamento 3D Carbono Urbano", descricao: "Laser scanner em manutenção - sem previsão de retorno", severidade: "Crítica", responsavel: "TI", prazo: "15/07/2026" },
  { id: "bl-02", acao: "Levantamento 3D Carbono Urbano", descricao: "Servidor de processamento com capacidade insuficiente", severidade: "Alta", responsavel: "Infraestrutura", prazo: "20/07/2026" },
  { id: "bl-03", acao: "Parque Ambiental Norte", descricao: "Acesso restrito a 30% da área por determinação judicial", severidade: "Alta", responsavel: "Jurídico", prazo: "Sem prazo" },
  { id: "bl-04", acao: "CTA Goiânia Centro", descricao: "Aguardando liberação da CET para vias de grande fluxo", severidade: "Média", responsavel: "Coord. Técnico", prazo: "10/07/2026" },
];

export const overviewProgressByAction = [
  { acao: "CTA Goiânia Centro", progresso: 65, etapa: "Cadastro de Campo", status: "em_andamento" },
  { acao: "Inventário Praça Universitária", progresso: 88, etapa: "Validação", status: "em_validacao" },
  { acao: "Parque Ambiental Norte", progresso: 42, etapa: "Cadastro de Campo", status: "em_andamento" },
  { acao: "Corredor Verde Sul", progresso: 28, etapa: "Levantamento Base", status: "em_andamento" },
  { acao: "Árvores Críticas Setor Bueno", progresso: 90, etapa: "Validação", status: "em_validacao" },
  { acao: "Compensação Ambiental Vila Nova", progresso: 48, etapa: "Cadastro de Campo", status: "em_andamento" },
  { acao: "Levantamento 3D Carbono Urbano", progresso: 35, etapa: "Processamento 3D", status: "bloqueada" },
];

export const stagesByStatus = [
  { status: "Concluída", quantidade: 42, cor: "bg-green-500" },
  { status: "Em andamento", quantidade: 12, cor: "bg-orange-500" },
  { status: "Em revisão", quantidade: 3, cor: "bg-yellow-500" },
  { status: "Planejada", quantidade: 4, cor: "bg-blue-500" },
  { status: "Não iniciada", quantidade: 6, cor: "bg-gray-400" },
  { status: "Bloqueada", quantidade: 1, cor: "bg-red-500" },
];

export const actionTypes = [
  "Implantação do CTA Municipal",
  "Inventário Arbóreo Urbano",
  "Inventário de Praça",
  "Inventário de Parque",
  "Inventário de UC",
  "Monitoramento de Árvores de Risco",
  "Compensação Ambiental",
  "Projeto de Carbono Urbano",
  "Levantamento 3D Arbóreo",
];

export const spatialUnitTypes = [
  "Município", "Distrito", "Bairro", "Setor", "Logradouro",
  "Trecho de via", "Quadra", "Lote", "Praça", "Parque",
  "Canteiro central", "Área verde", "Área institucional",
  "Unidade escolar", "Cemitério", "Área de preservação",
];

export const publicationChecklist: ChecklistItem[] = [
  { id: "pg-01", descricao: "Publicar mapa arbóreo interativo", concluido: true, responsavel: "Analista GIS", prazo: "D+5", observacao: "Publicado no portal FloraIQ" },
  { id: "pg-02", descricao: "Gerar dashboard gerencial", concluido: true, responsavel: "Coordenador Técnico", prazo: "D+3", observacao: "KPIs e gráficos configurados" },
  { id: "pg-03", descricao: "Gerar relatórios técnicos", concluido: false, responsavel: "Engenheiro Florestal", prazo: "D+10", observacao: "Em revisão" },
  { id: "pg-04", descricao: "Criar camadas temáticas", concluido: true, responsavel: "Analista GIS", prazo: "D+7", observacao: "Risco, poda, supressão e espécies" },
  { id: "pg-05", descricao: "Criar painel de risco", concluido: true, responsavel: "Coordenador Técnico", prazo: "D+5", observacao: "Painel com alertas configurados" },
  { id: "pg-06", descricao: "Criar painel de manejo", concluido: false, responsavel: "Engenheiro Florestal", prazo: "D+12", observacao: "Plano de manejo pendente" },
  { id: "pg-07", descricao: "Criar indicadores de gestão", concluido: false, responsavel: "Gerente de Projeto", prazo: "D+15", observacao: "Indicadores para prefeitura" },
  { id: "pg-08", descricao: "Disponibilizar dados para gestão municipal", concluido: false, responsavel: "Analista GIS", prazo: "D+20", observacao: "Exportar camadas para prefeitura" },
  { id: "pg-09", descricao: "Publicar portal público (se autorizado)", concluido: false, responsavel: "Gerente de Projeto", prazo: "D+25", observacao: "Aguardando autorização da prefeitura" },
  { id: "pg-10", descricao: "Exportar camadas GIS", concluido: true, responsavel: "Analista GIS", prazo: "D+8", observacao: "GeoJSON + Shapefile exportados" },
];